/**
 * Set new password: (1) Admin "require password change" – user already logged in.
 * (2) User "forgot password" – user lands from email link with recovery hash; we set session then show form.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import PasswordRequirementsHint from '@/components/PasswordRequirementsHint'

function parseHash(hash: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!hash || hash.charAt(0) !== '#') return params
  hash.slice(1).split('&').forEach((pair) => {
    const [key, value] = pair.split('=')
    if (key && value) params[decodeURIComponent(key)] = decodeURIComponent(value.replace(/\+/g, ' '))
  })
  return params
}

export default function SetPasswordPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [recoveryChecked, setRecoveryChecked] = useState(false)
  const [recoveryError, setRecoveryError] = useState<string | null>(null)
  const [fromRecoveryLink, setFromRecoveryLink] = useState(false)

  // Handle email reset link: hash contains access_token, refresh_token, type=recovery
  useEffect(() => {
    const hash = window.location.hash
    const params = parseHash(hash)
    if (params.type === 'recovery' && params.access_token && params.refresh_token) {
      supabase.auth
        .setSession({ access_token: params.access_token, refresh_token: params.refresh_token })
        .then(() => {
          window.history.replaceState(null, '', window.location.pathname)
          setFromRecoveryLink(true)
          setRecoveryChecked(true)
        })
        .catch((err) => {
          setRecoveryError(err.message || 'Invalid or expired reset link.')
          setRecoveryChecked(true)
        })
    } else {
      setRecoveryChecked(true)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    setLoading(true)

    const { error: updateError } = await supabase.auth.updateUser({ password })

    if (updateError) {
      setError(updateError.message)
      setLoading(false)
      return
    }

    const { data: { user } } = await supabase.auth.getUser()
    if (user?.id) {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ must_change_password: false })
        .eq('id', user.id)

      if (!profileError) {
        queryClient.setQueryData(
          ['profile-suspended-check', user.id],
          (old: { is_suspended: boolean; status: string | null; must_change_password?: boolean } | null) =>
            old != null ? { ...old, must_change_password: false } : { is_suspended: false, status: 'active', must_change_password: false }
        )
      }
    }

    setLoading(false)
    navigate('/', { replace: true })
  }

  if (!recoveryChecked) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Set new password</h1>
        <p className="text-text-primary">Checking your link…</p>
      </div>
    )
  }

  if (recoveryError) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Set new password</h1>
        <p role="alert" className="text-color-error mb-4">{recoveryError}</p>
        <p className="text-text-primary mb-4">
          <Link to="/forgot-password" className="text-text-link underline font-semibold">Request a new link</Link>
          {' or '}
          <Link to="/login" className="text-text-link underline font-semibold">back to login</Link>.
        </p>
      </div>
    )
  }

  if (recoveryChecked && !user && !fromRecoveryLink) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Set new password</h1>
        <p className="text-text-primary mb-4">
          Use the link from your reset email to set a new password, or log in if you already have an account.
        </p>
        <p className="text-text-primary">
          <Link to="/forgot-password" className="text-text-link underline font-semibold">Forgot password</Link>
          {' · '}
          <Link to="/login" className="text-text-link underline font-semibold">Login</Link>
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6">Set new password</h1>
      <p className="text-text-primary mb-4">
        Enter your new password below.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="password" className="block mb-2 font-semibold">
            New password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <div className="flex gap-2">
            <input
              type={passwordVisible ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
              minLength={8}
              className="w-full flex-1"
              autoComplete="new-password"
              aria-describedby="set-password-hint"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              className="px-3 py-2 rounded border shrink-0"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            >
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          <PasswordRequirementsHint id="set-password-hint" />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block mb-2 font-semibold">
            Confirm new password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <input
            type={passwordVisible ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            aria-required="true"
            minLength={8}
            className="w-full"
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div role="alert" className="text-color-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-bg-interactive text-text-inverse hover:bg-bg-interactive-hover disabled:opacity-50"
        >
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  )
}
